import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Button, Card, Checkbox, DatePicker, Empty, Space, Switch, Tag, Typography } from "antd";
import { CameraOutlined, FilePdfOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import CustomerSelect from "../../../commons/Billing/CustomerSelect";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { getMeta, listDocs, photoUrl } from "./api";
import ReportDialog from "./ReportDialog";
import { PendingTag } from "../../../offline/OfflineNotice";
import "./siteDocs.css";

const LIST = "/user/site-docs";
// Other pages link here filtered: /user/site-docs?project=… (or client, offer, invoice)
const FILTER_KEYS = ["client", "project", "offer", "invoice"];

const Cover = ({ item }) =>
  item.cover ? (
    <img className="sd-cover" src={photoUrl(item._id, item.cover)} alt="" loading="lazy" />
  ) : (
    <span className="sd-cover sd-cover--empty" aria-hidden="true">
      <CameraOutlined />
    </span>
  );

const SiteDocs = () => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return Object.fromEntries(FILTER_KEYS.map(key => [key, params.get(key) || ""]));
  });
  const [mine, setMine] = useState(false);
  const [range, setRange] = useState(null);
  const [selected, setSelected] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ data: [], total: 0, pageSize: 50 });
  const [loading, setLoading] = useState(false);

  const listFilters = {
    searchText,
    ...filters,
    mine,
    from: range?.[0] ? range[0].format("YYYY-MM-DD") : "",
    to: range?.[1] ? range[1].format("YYYY-MM-DD") : ""
  };

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listDocs({ ...listFilters, page })
      .then(res => !cancelled && setResult(res.data))
      .catch(generalErrorAlert)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(listFilters), page]);

  const setFilter = patch => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPage(1);
    setSelected([]);
  };

  // What the report covers: the ticked entries, else everything the filters show
  const reportSelection = selected.length ? { ids: selected } : { filters: listFilters };
  const reportCount = selected.length || result.total;
  const filterProject = (meta?.projects || []).find(p => String(p._id) === filters.project);
  // The customer the report is for: from the filters, else the one all ticked entries share
  const selectedCustomers = new Set(
    result.data.filter(d => selected.includes(d._id)).map(d => d.clientInfo?._id || "")
  );
  const reportCustomerId =
    filters.client ||
    filterProject?.client ||
    (selectedCustomers.size === 1 ? [...selectedCustomers][0] : "");
  const reportCustomer = (meta?.clients || []).find(
    c => String(c._id) === String(reportCustomerId || "")
  );
  const toggle = (id, checked) =>
    setSelected(prev => (checked ? [...prev, id] : prev.filter(key => key !== id)));

  const linkedFilter = FILTER_KEYS.slice(1).find(key => filters[key]);
  const newUrl = () => {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([, value]) => value)
    ).toString();
    return `${LIST}/new${params ? `?${params}` : ""}`;
  };

  const columns = [
    {
      title: t("Documentation"),
      key: "doc",
      mobile: "title",
      render: (_, item) => (
        <div className="sd-list-item">
          {!isMobile && <Cover item={item} />}
          <div className="sd-list-text">
            <Link to={`${LIST}/${item._id}`}>
              <Typography.Text strong>{item.title || item.number}</Typography.Text>
            </Link>{" "}
            <Typography.Text type="secondary">
              {item.date ? dayjs(item.date).format("DD.MM.YYYY") : ""}
            </Typography.Text>
            <PendingTag module="site-docs" record={item} />
            <div className="sd-small sd-clamp">{item.notes}</div>
          </div>
        </div>
      )
    },
    {
      title: t("Customer"),
      key: "client",
      mobile: "subtitle",
      render: (_, item) =>
        item.clientInfo
          ? [item.clientInfo.name, item.clientInfo.branch].filter(Boolean).join(" · ")
          : "—"
    },
    {
      title: t("Assigned to"),
      key: "links",
      mobile: "full",
      render: (_, item) => {
        const tags = [
          item.projectInfo && { color: "blue", text: `${t("Project")}: ${item.projectInfo.name}` },
          item.offerInfo && { color: "gold", text: `${t("Offer")}: ${item.offerInfo.label}` },
          item.invoiceInfo && { color: "green", text: `${t("Invoice")}: ${item.invoiceInfo.label}` }
        ].filter(Boolean);
        return tags.length ? (
          <Space size={[4, 4]} wrap>
            {tags.map(tag => (
              <Tag key={tag.text} color={tag.color} className="sd-tag">
                {tag.text}
              </Tag>
            ))}
          </Space>
        ) : (
          "—"
        );
      }
    },
    {
      title: t("Photos"),
      key: "photos",
      mobile: "badge",
      width: 90,
      align: "center",
      render: (_, item) => (
        <Tag icon={<CameraOutlined />} className="sd-tag">
          {item.photoCount}
        </Tag>
      )
    },
    {
      title: t("By"),
      key: "author",
      width: 160,
      render: (_, item) => item.createdByName || "—"
    }
  ];

  // Phones show cards: selection is a checkbox in the card footer instead of a table column
  if (isMobile) {
    columns.push({
      title: "",
      key: "actions",
      mobile: "actions",
      render: (_, item) => (
        <Checkbox
          checked={selected.includes(item._id)}
          onClick={event => event.stopPropagation()}
          onChange={event => toggle(item._id, event.target.checked)}
        >
          {t("Select for report")}
        </Checkbox>
      )
    });
  }

  const filtered = Boolean(searchText || mine || range || FILTER_KEYS.some(key => filters[key]));
  const isEmpty = !loading && !result.data.length && !filtered;

  return (
    <div className="site-docs-page">
      <PageToolbar
        filters={
          <Space direction="vertical" className="sd-full">
            <SearchBarComp
              handleSearch={value => {
                setSearchText(value);
                setPage(1);
              }}
            />
            <Space wrap className="sd-filters">
              <CustomerSelect
                className="sd-customer-filter"
                customers={meta?.clients || []}
                value={filters.client || undefined}
                placeholder={t("All customers")}
                onChange={clientId =>
                  setFilter({ client: clientId || "", project: "", offer: "", invoice: "" })
                }
              />
              {linkedFilter && (
                <Tag closable onClose={() => setFilter({ [linkedFilter]: "" })} color="blue">
                  {t("Filtered by {{what}}", {
                    what: t(
                      { project: "Project", offer: "Offer", invoice: "Invoice" }[linkedFilter]
                    )
                  })}
                </Tag>
              )}
              <DatePicker.RangePicker
                className="sd-range"
                format="DD.MM.YYYY"
                value={range}
                inputReadOnly
                placeholder={[t("From"), t("To")]}
                onChange={value => {
                  setRange(value);
                  setPage(1);
                  setSelected([]);
                }}
              />
              {meta?.can.readAll && (
                <Space size={6}>
                  <Switch
                    size="small"
                    checked={mine}
                    onChange={value => {
                      setMine(value);
                      setPage(1);
                    }}
                  />
                  <Typography.Text>{t("Only mine")}</Typography.Text>
                </Space>
              )}
            </Space>
          </Space>
        }
        actions={
          <Space wrap>
            {result.total > 0 && (
              <Button icon={<FilePdfOutlined />} onClick={() => setReportOpen(true)}>
                {selected.length
                  ? t("Photo report of {{n}} selected", { n: selected.length })
                  : t("Photo report (PDF)")}
              </Button>
            )}
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push(newUrl())}>
                {t("New documentation")}
              </Button>
            )}
          </Space>
        }
      />
      {selected.length > 0 && (
        <div className="sd-selection mb-3">
          <Typography.Text>
            {t("{{n}} selected for the report", { n: selected.length })}
          </Typography.Text>
          <Button size="small" type="link" onClick={() => setSelected([])}>
            {t("Clear")}
          </Button>
        </div>
      )}

      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>{t("Document your work on site")}</Typography.Title>
                <Typography.Paragraph type="secondary" className="sd-empty-text">
                  {t(
                    "Take photos, type or speak what you did, and assign it to the customer and their project, offer or invoice."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push(newUrl())}>
                {t("New documentation")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        (() => {
          const table = (
            <ResponsiveTable
              rowKey="_id"
              loading={loading}
              columns={columns}
              dataSource={result.data}
              mobileAvatar={item => <Cover item={item} />}
              rowSelection={
                isMobile
                  ? undefined
                  : {
                      selectedRowKeys: selected,
                      preserveSelectedRowKeys: true,
                      onChange: keys => setSelected(keys)
                    }
              }
              onRow={record => ({
                onClick: isMobile ? () => history.push(`${LIST}/${record._id}`) : undefined,
                onDoubleClick: () => history.push(`${LIST}/${record._id}`)
              })}
              pagination={
                result.total > result.pageSize
                  ? {
                      current: page,
                      pageSize: result.pageSize,
                      total: result.total,
                      showSizeChanger: false,
                      onChange: setPage
                    }
                  : false
              }
              scroll={{ x: 760 }}
            />
          );
          return isMobile ? table : <Card styles={{ body: { padding: 0 } }}>{table}</Card>;
        })()
      )}

      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        selection={reportSelection}
        count={reportCount}
        defaultTitle={filterProject?.name || ""}
        defaultEmail={reportCustomer?.email || ""}
        canEmail={meta?.can.email}
      />
    </div>
  );
};

export default SiteDocs;
